import { Text, toast, type TextLabelStyle } from "@carbon6/c6-frontend-design";
import { createElement, Fragment, type ReactNode } from "react";
import { appTextModes } from "@/shared/ui/text";
import { MAX_WINDOWS, WindowStackManager } from "@/shared/ui/window";

/**
 * 앱 스코프 전체화면 창 스택 — 제네릭 `WindowStackManager` 에 앱의 kind 집합과 cap 안내(toast)를 입힌다.
 * 매니저 자체는 순수(kind=불투명 문자열, 안내 없음)라 이 모듈이 앱 정책(어떤 창 종류 · 초과 시 토스트)을
 * 소유한다. 소비처는 여기의 `windowStack` 싱글턴과 헬퍼만 쓴다.
 *
 * `PageBase` · `Gnb` 와 함께 modules 가 import 할 수 있는 전역 셸 세 모듈 중 하나다(규칙 §3) — 창을
 * 여는 것도 라우트 페이지가 셸에 말하는 계약이다.
 */

// 앱이 스택에 올리는 창 종류. `AppWindowHost` 의 레지스트리가 이 값으로 콘텐츠를 매핑한다.
export type WindowKind = "datasetDetail";

type WindowStackHotData = {
  windowStack?: WindowStackManager;
};

// React 리마운트 · Vite 모듈 교체(HMR)에도 스택을 보존한다 — 열린 창이 코드 저장마다 사라지지 않게.
const hotData = import.meta.hot?.data as WindowStackHotData | undefined;
export const windowStack = hotData?.windowStack ?? new WindowStackManager();
if (hotData !== undefined) {
  hotData.windowStack = windowStack;
}

const windowTaskbarLabelStyle = {
  prefix: {
    as: "strong",
    style: { color: "var(--c6-color-text-accent)" },
  },
} satisfies TextLabelStyle;

/** 작업표시줄 라벨 — 강조 접두(무엇의 창인지) + 제목. */
export function windowTaskbarTextLabel(prefix: string, title: string): ReactNode {
  return createElement(
    Text,
    {
      variant: appTextModes["caption-bold"],
      labelStyle: windowTaskbarLabelStyle,
    },
    "<prefix>" + prefix + "</prefix> ",
    createElement(Fragment, null, title),
  );
}

// cap 초과로 못 열린 경우의 사용자 안내. 매니저는 null 만 돌려주고 안내는 여기서 한다.
export function notifyWindowCapReached(): void {
  toast.info(
    `전체 화면 창은 최대 ${MAX_WINDOWS}개까지 열 수 있습니다. 기존 창을 닫고 다시 시도해 주세요.`,
  );
}
